import { useEffect, useMemo, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa6";

type Props = {
  src: string;
  waveData?: string | null;
  durationLabel?: string | null;
  isOwn?: boolean;
  theme?: string;
};

const BAR_W = 2;
const BAR_GAP = 1;
const BAR_COUNT_DEFAULT = 48;
const BAR_COUNT_MIN = 28;
const BAR_COUNT_MAX = 72;
const peakCache = new Map<string, number[]>();

function resampleBars(src: number[], count: number): number[] {
  if (count <= 0) return [];
  if (!src.length) return Array.from({ length: count }, () => 0.2);
  if (src.length === count) return src;
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor((i / count) * src.length);
    out.push(src[Math.min(idx, src.length - 1)]);
  }
  return out;
}

function barsFitInWidth(widthPx: number): number {
  if (widthPx <= 0) return BAR_COUNT_DEFAULT;
  const n = Math.floor((widthPx + BAR_GAP) / (BAR_W + BAR_GAP));
  return Math.max(BAR_COUNT_MIN, Math.min(BAR_COUNT_MAX, n));
}

function parseWave(raw?: string | null): number[] {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n))
    .map((n) => Math.max(0, Math.min(1, n)));
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec % 60);
  const m = Math.floor(sec / 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseDurationLabel(label?: string | null): number | null {
  if (!label) return null;
  const parts = String(label).trim().split(":").map(Number);
  if (parts.some((n) => !Number.isFinite(n))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

/** Downsample PCM peaks into N bars (0…1). */
function peaksFromAudioBuffer(buffer: AudioBuffer, bars = BAR_COUNT): number[] {
  const channel = buffer.getChannelData(0);
  if (!channel.length) return [];
  const block = Math.floor(channel.length / bars) || 1;
  const out: number[] = [];
  let peakMax = 0.0001;

  for (let i = 0; i < bars; i++) {
    const start = i * block;
    const end = Math.min(start + block, channel.length);
    let peak = 0;
    for (let j = start; j < end; j++) {
      const v = Math.abs(channel[j]);
      if (v > peak) peak = v;
    }
    out.push(peak);
    if (peak > peakMax) peakMax = peak;
  }

  // Normalize + slight lift so quiet parts stay visible
  return out.map((p) => {
    const n = p / peakMax;
    return Math.max(0.08, Math.min(1, Math.pow(n, 0.7)));
  });
}

async function extractPeaksFromSrc(src: string): Promise<number[]> {
  const cached = peakCache.get(src);
  if (cached) return cached;

  const res = await fetch(src, { mode: "cors", credentials: "omit" });
  if (!res.ok) throw new Error(`wave fetch ${res.status}`);
  const ab = await res.arrayBuffer();

  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  const ctx = new Ctx();
  try {
    const decoded = await ctx.decodeAudioData(ab.slice(0));
    const peaks = peaksFromAudioBuffer(decoded, BAR_COUNT_MAX);
    peakCache.set(src, peaks);
    return peaks;
  } finally {
    void ctx.close();
  }
}

/**
 * Telegram-like voice bubble innards.
 * Prefers server wave_data; if empty — builds real peaks from the audio file.
 */
export default function VoiceMessagePlayer({
  src,
  waveData,
  durationLabel,
  isOwn = false,
  theme = "light",
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const waveSlotRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(
    () => parseDurationLabel(durationLabel) ?? 0,
  );
  const [slotWidth, setSlotWidth] = useState(0);

  const fromServer = useMemo(() => parseWave(waveData), [waveData]);
  const [fromFile, setFromFile] = useState<number[] | null>(null);

  useEffect(() => {
    const el = waveSlotRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setSlotWidth(Math.floor(w));
    });
    ro.observe(el);
    setSlotWidth(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setFromFile(null);
    if (fromServer.length > 0) return;

    let cancelled = false;
    extractPeaksFromSrc(src)
      .then((peaks) => {
        if (!cancelled && peaks.length) setFromFile(peaks);
      })
      .catch(() => {
        /* CORS / decode fail — keep flat placeholder */
        if (!cancelled) setFromFile(null);
      });

    return () => {
      cancelled = true;
    };
  }, [src, fromServer.length]);

  const sourceBars =
    fromServer.length > 0
      ? fromServer
      : fromFile && fromFile.length > 0
        ? fromFile
        : Array.from({ length: BAR_COUNT_DEFAULT }, () => 0.2);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = "metadata";
    audioRef.current = audio;
    let raf = 0;

    const tick = () => {
      const d = audio.duration || duration || 1;
      if (d > 0) setProgress(audio.currentTime / d);
      if (!audio.paused && !audio.ended) {
        raf = requestAnimationFrame(tick);
      }
    };

    const onPlay = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    const onPause = () => {
      cancelAnimationFrame(raf);
      const d = audio.duration || duration || 1;
      if (d > 0) setProgress(audio.currentTime / d);
    };
    const onMeta = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const onEnd = () => {
      cancelAnimationFrame(raf);
      setPlaying(false);
      setProgress(0);
      audio.currentTime = 0;
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);

    return () => {
      cancelAnimationFrame(raf);
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audioRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    const fromLabel = parseDurationLabel(durationLabel);
    if (fromLabel != null && duration <= 0) setDuration(fromLabel);
  }, [durationLabel, duration]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      void audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
  };

  const seek = (clientX: number) => {
    const el = waveRef.current;
    const audio = audioRef.current;
    if (!el || !audio) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const d = audio.duration || duration || 0;
    if (d > 0) {
      audio.currentTime = ratio * d;
      setProgress(ratio);
    }
  };

  const dark = theme === "dark";
  const playBg = isOwn
    ? dark
      ? "bg-white/95"
      : "bg-white"
    : "bg-[var(--theme-color,#50a2e3)]";
  const playIcon = isOwn
    ? "text-black/80"
    : "text-[var(--theme-text-color,#fff)]";
  const barIdle = isOwn
    ? "bg-black/25"
    : dark
      ? "bg-white/30"
      : "bg-black/25";
  const barPlayed = isOwn
    ? "bg-black/70"
    : dark
      ? "bg-white/85"
      : "bg-black/55";
  const metaColor = isOwn
    ? "text-black/55"
    : dark
      ? "text-white/50"
      : "text-black/45";

  const displayTime =
    playing || progress > 0
      ? formatTime(audioRef.current?.currentTime || 0)
      : formatTime(duration || parseDurationLabel(durationLabel) || 0);

  const barCount = barsFitInWidth(slotWidth);
  const displayBars = useMemo(
    () => resampleBars(sourceBars, barCount),
    [sourceBars, barCount],
  );
  const waveTrackPx =
    displayBars.length * BAR_W +
    Math.max(0, displayBars.length - 1) * BAR_GAP;

  return (
    <div className="flex w-[min(17.5rem,70vw)] items-center gap-2.5 py-0.5">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className={`grid h-11 w-11 shrink-0 place-content-center rounded-full ${playBg} shadow-sm transition-transform active:scale-95`}
      >
        {playing ? (
          <FaPause className={`h-4 w-4 ${playIcon}`} />
        ) : (
          <FaPlay className={`h-4 w-4 translate-x-[1px] ${playIcon}`} />
        )}
      </button>

      <div ref={waveSlotRef} className="min-w-0 flex-1">
        <div
          ref={waveRef}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          tabIndex={0}
          className="relative h-7 max-w-full cursor-pointer"
          style={{ width: waveTrackPx || "100%" }}
          onClick={(e) => seek(e.clientX)}
          onKeyDown={(e) => {
            const audio = audioRef.current;
            if (!audio) return;
            if (e.key === "ArrowRight") {
              audio.currentTime = Math.min(
                audio.duration || duration,
                audio.currentTime + 1,
              );
            }
            if (e.key === "ArrowLeft") {
              audio.currentTime = Math.max(0, audio.currentTime - 1);
            }
          }}
        >
          {/* Idle — fixed 2px bars + 1px gaps */}
          <div className="absolute inset-0 flex items-center">
            {displayBars.map((v, i) => (
              <span
                key={`i-${i}`}
                className={`inline-block shrink-0 rounded-full ${barIdle}`}
                style={{
                  width: BAR_W,
                  marginRight: i < displayBars.length - 1 ? BAR_GAP : 0,
                  height: Math.max(3, Math.round(4 + v * 22)),
                }}
              />
            ))}
          </div>
          {/* Played — same geometry, smooth clip by progress (not bar steps) */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              clipPath: `inset(0 ${Math.max(0, (1 - progress) * 100)}% 0 0)`,
            }}
          >
            <div className="flex h-full items-center">
              {displayBars.map((v, i) => (
                <span
                  key={`p-${i}`}
                  className={`inline-block shrink-0 rounded-full ${barPlayed}`}
                  style={{
                    width: BAR_W,
                    marginRight: i < displayBars.length - 1 ? BAR_GAP : 0,
                    height: Math.max(3, Math.round(4 + v * 22)),
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          className={`mt-0.5 text-[11px] leading-none tabular-nums ${metaColor}`}
        >
          {displayTime}
        </div>
      </div>
    </div>
  );
}
